import _ from 'underscore'

import React, { useCallback, useEffect, useState } from 'react'

import styled from 'styled-components'

import Modal from 'components/Modal'
import ModalButton from 'components/Modal/Button'
import TextInput from 'components/TextInput'
import Feature from 'components/UserInfoMenu/FeaturesManager/Feature'

import { useSessionContext } from 'hooks/SessionContext'
import useDealerFeatures from 'hooks/useDealerFeatures'

import { scrubText } from 'helpers/inputCleaners'

import { ButtonContainer } from 'styles/shared'

import Api from 'services/api'
import Spinner from 'components/Spinner'

type Props = {
  closeCallback: (reloadPage) => void
}

const FeaturesModal = ({ closeCallback }: Props) => {
  const { session: { dealerId } } = useSessionContext()
  const { features: oldEnabledFeatures, isFeaturesLoaded } = useDealerFeatures(dealerId)

  const [isAddingFeature, setIsAddingFeature] = useState(false)
  const [inputFeatureCode, setInputFeatureCode] = useState<string | null>(null)
  const [newEnabledFeatures, setNewEnabledFeatures] = useState<Array<string>>([])
  const [note, setNote] = useState('')

  useEffect(() => {
    if (isFeaturesLoaded) {
      setNewEnabledFeatures(oldEnabledFeatures || [])
      setNote('')
    }
  }, [oldEnabledFeatures, isFeaturesLoaded])

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.currentTarget.readOnly) {
      await onClickEnableFeature()
    }
  }

  const onClickClose = () => {
    const reloadPage = newEnabledFeatures.length !== oldEnabledFeatures?.length || _.difference(newEnabledFeatures, oldEnabledFeatures).length > 0
    closeCallback(reloadPage)
  }

  const onClickEnableFeature = useCallback(async () => {
    if (_.isEmpty(inputFeatureCode)) {
      return
    }

    setIsAddingFeature(true)
    setNote('')

    await Api.FeatureCodes.addDealerFeature(dealerId, inputFeatureCode)
      .then((result) => {
        setNewEnabledFeatures(result.features)
        setInputFeatureCode('')
        setNote('Feature added successfully')
      }).catch(async (error) => {
        if (error.status === 400) {
          const errorResponse = await error.response.json()
          setNote(errorResponse?.base[0].name)
        }
      }).finally(() => {
        setIsAddingFeature(false)
      })
  }, [inputFeatureCode, dealerId])

  const onClickRemoveFeature = useCallback((removeFeatureCode) => {
    return Api.FeatureCodes.removeDealerFeature(dealerId, removeFeatureCode)
      .then((result) => {
        setNewEnabledFeatures(result.features)
        setInputFeatureCode('')
        setNote(`Feature '${removeFeatureCode}' successfully removed`)
      })
  }, [dealerId])

  return (
    <Modal title="Beta Features" variant="sm" isDisplayed closeCallback={onClickClose}>
      {isFeaturesLoaded && (
        <div>
          {!_.isEmpty(newEnabledFeatures) && (
            <div>
              <S.SectionTitle>Enabled Features</S.SectionTitle>
              <S.Features>
                {newEnabledFeatures.map((feature) => <Feature key={feature} feature={feature} onRemoveFeatureCallback={onClickRemoveFeature} />)}
              </S.Features>
            </div>)
          }

          <S.Note>{note}</S.Note>
          <TextInput
            autoFocus
            readOnly={isAddingFeature}
            label="Feature Code"
            value={inputFeatureCode ?? ''}
            onChange={(event) => setInputFeatureCode(scrubText(event.target.value))}
            onKeyDown={handleKeyDown}
          />

          <ButtonContainer noBorder>
            <ModalButton decoration="secondary" text="Close" onPress={onClickClose} />
            <ModalButton decoration="positive" text="Enable Feature" onPress={onClickEnableFeature} showSpinner={isAddingFeature} />
          </ButtonContainer>
        </div>
      )}
      {!isFeaturesLoaded && <Spinner variant="page" />}
    </Modal>
  )
}

export default FeaturesModal

const S = {
  Features: styled.div`
    display: inline-block;
  `,
  Note: styled.div`
    margin: 5px 0px 5px 0px;
  `,
  SectionTitle: styled.h2`
    text-transform: capitalize;
  `
}
